/**
 * The toast stack (right, under the clock): "transmission" cards from `state/toasts.ts`. A card
 * flickers in with a scanline sweep and its line types out (an offer's is there at once); its
 * border, left bar and header are in its tone — what the news means: green good, red loss /
 * danger, cyan ally / offer, amber warning, dim info — and the nation it is from / about is only
 * its pattern swatch; a draining line at the bottom. Newest on top, older ones fold into one
 * line; the offer card carries [Y ACCEPT] [N DECLINE] (and › NEXT with several). Cards that go fade and slide out. Only the cards
 * take the pointer — the map under the stack stays clickable.
 */
import type { PlayerId } from '@sa/sim';
import type { ToastCard, ToastFrame } from '../state/toasts';
import { drawPatternSwatch, type Palette } from '../render/palette';
import { h, setText } from './dom';

/** the slide-out (ms; matches the CSS) */
const EXIT_MS = 420;

interface CardEl {
  el: HTMLElement;
  count: HTMLElement;
  timer: HTMLElement;
  life: HTMLElement;
  layout: string;
  /** what it says (title, line, sub, tone, nation): a change rebuilds it */
  words: string;
  leaving: boolean;
}

export class ToastStack {
  readonly el = h('div.hud-toasts');
  private cards = new Map<string, CardEl>();

  constructor(private pal: Palette) {}

  update(f: ToastFrame): void {
    const live = new Set(f.cards.map((c) => c.id));
    for (const [id, c] of this.cards) {
      if (live.has(id) || c.leaving) continue;
      c.leaving = true;
      c.el.classList.add('out');
      c.el.style.order = '99';
      setTimeout(() => {
        c.el.remove();
        this.cards.delete(id);
      }, EXIT_MS);
    }
    f.cards.forEach((card, i) => {
      let c = this.cards.get(card.id);
      // (rebuilt only when what it shows changes: its shape — compact, buttons — or its words, as a
      // wave of damage grows on one card: then it blinks once instead of a second reveal)
      const layout = `${card.compact}|${card.buttons?.map((b) => b.key ?? '').join() ?? ''}`;
      const words = `${card.title}|${card.text}|${card.sub ?? ''}|${card.tone}|${card.from ?? ''}`;
      const reworded = !!c && c.words !== words;
      if (c && (c.layout !== layout || reworded)) {
        c.el.remove();
        c = undefined;
      }
      if (!c) {
        c = this.build(card, layout, !this.cards.has(card.id));
        c.words = words;
        if (reworded) c.el.classList.add('bump');
        this.cards.set(card.id, c);
        this.el.append(c.el);
      }
      // order by flex `order`, not by moving nodes (a moved node would replay its reveal)
      c.el.style.order = String(i);
      setText(c.count, card.count > 1 ? `×${card.count}` : '');
      setText(c.timer, card.timer ?? '');
      c.life.style.transform = `scaleX(${card.life.toFixed(3)})`;
    });
  }

  private build(card: ToastCard, layout: string, fresh: boolean): CardEl {
    const tone = card.tone;
    const count = h('span.cnt');
    const timer = h('span.tmr');
    const life = h('i.life');
    // a folded card drops its header word (the line's key words must survive a narrow card); the swatch stays
    const head = h('div.th', {}, card.from !== undefined ? this.swatch(card.from) : null, card.compact ? null : h('span.ttl', {}, card.title), count, timer);
    const body = h('div.tb', {}, h('span', {}, card.text));
    // (custom properties need setProperty) the typing runs one step per letter
    body.style.setProperty('--n', String(Math.max(1, card.text.length)));
    const buttons = card.buttons?.length
      ? h(
          'div.tacts',
          {},
          ...card.buttons.map((b) => {
            const btn = h('button.tact', {}, b.key ? h('span.key', {}, b.key.toUpperCase()) : null, b.label);
            btn.onclick = (e) => {
              e.stopPropagation();
              b.run();
            };
            return btn;
          }),
        )
      : null;
    const el = h(
      `div.toast.tone-${tone}.k-${card.kind}${card.compact ? '.compact' : ''}${fresh ? '' : '.settled'}`,
      {},
      head,
      body,
      card.sub && !card.compact ? h('div.ts', {}, card.sub) : null,
      buttons,
      life,
    );
    // (the card's colour is what the news means — its tone; the nation is only its swatch)
    // the cards take the pointer (buttons), and a click on one never reaches the map
    el.addEventListener('pointerdown', (e) => e.stopPropagation());
    return { el, count, timer, life, layout, words: '', leaving: false };
  }

  private swatch(p: PlayerId): HTMLCanvasElement {
    const sw = h('canvas.sw', { width: 10, height: 10 });
    drawPatternSwatch(sw.getContext('2d')!, this.pal.pattern(p), this.pal.color(p));
    return sw;
  }
}
